import React, {
  FC,
  useEffect,
  useState,
  useCallback,
  FormEvent,
  ReactNode
} from 'react'
import { useRouter } from 'next/router'
import { IMaskInput } from 'react-imask'
import CurrencyInput from 'react-currency-input-field'
import { useToasts } from 'react-toast-notifications'
import cl from 'classnames'
import ReactSelect from 'react-select'

import { api } from '@services/api'

import styles from './styles.module.scss'
import { styles as stylesSelect } from '@components/form/selects/select/styles'
import { BeatLoader } from 'react-spinners'
import { validateEmail } from '@utils/validations'
import Image from 'next/image'

interface FormData {
  postalCode: string
  lightBill: string
  powerDistributorId: string
  powerDistributor: string
  email: string
}

interface FormErrors {
  postalCode: boolean
  lightBill: boolean
  email: boolean
  powerDistributorId: boolean
}

interface Props {
  background: { path: string; alt: string }
  headerContent?: ReactNode
}

const SimulationBanner: FC<Props> = ({ background, headerContent }) => {
  const router = useRouter()
  const { addToast } = useToasts()
  const [loading, setLoading] = useState<boolean>(false)
  const [msgError, setMsgError] = useState<string>()

  const initialForm = {
    postalCode: '',
    lightBill: '',
    powerDistributorId: '',
    powerDistributor: '',
    email: ''
  }

  const [formData, setFormData] = useState<FormData>(initialForm)
  const [formErrors, setFormErrors] = useState<FormErrors>({
    lightBill: false,
    postalCode: false,
    email: false,
    powerDistributorId: false
  })

  const [powerDistributors, setPowerDistributors] = useState([])

  useEffect(() => {
    const postalCode = formData?.postalCode?.replace(/\D+/g, '')
    setMsgError(null)
    if (postalCode.length < 8) return
    setLoading(true)
    api
      .get(`/simulations/power_distributors?postalcode=${postalCode}`)
      .then(response => {
        if (response.data.city) {
          setPowerDistributors(response.data.power_distributors)
        } else {
          setPowerDistributors([])
          addToast('CEP não encontrado', {
            autoDismiss: true,
            appearance: 'warning'
          })
          setMsgError('CEP não encontrado')
        }
        setLoading(false)
      })
  }, [formData.postalCode])

  useEffect(() => {
    localStorage.setItem('origin-url', top.window.document.location.href)
  }, [])

  const setErrorsEntries = entries => {
    return Object.fromEntries(entries.map(([key]) => [key, true]))
  }

  const validateForm = (): boolean => {
    const dataRequired = Object.entries(formData).filter(([, value]) => !value)
    const errors = setErrorsEntries(dataRequired) as FormErrors
    const removedMask = formData?.postalCode.replace(/[^0-9]+/g, '')

    if (dataRequired.length) {
      setFormErrors({ ...errors })
      return false
    }

    if (removedMask.length < 8) {
      setFormErrors(oldErrors => ({ ...oldErrors, postalCode: true }))
      return false
    }

    if (!validateEmail(formData?.email)) {
      setFormErrors(oldErrors => ({ ...oldErrors, email: true }))
      return false
    }

    return true
  }

  const handleSubmit = useCallback(
    async (evt: FormEvent) => {
      evt.preventDefault()

      if (!validateForm()) return

      const power_distributor_id = +formData.powerDistributorId

      const power_distributor_name = powerDistributors.find(
        ({ id }) => id === power_distributor_id
      )?.name

      const simulation = {
        postalcode: formData.postalCode,
        light_bill: formData.lightBill,
        power_distributor_id,
        power_distributor_name,
        email: formData.email
      }

      router.push({
        pathname: '/calculadora-solar',
        query: { ...router.query, ...simulation }
      })
    },
    [formData]
  )

  const handleChangedValue = event => {
    setFormErrors(oldErrors => ({ ...oldErrors, [event.target.name]: false }))
    setFormData(data => ({ ...data, [event.target.name]: event.target.value }))
  }

  const handleSelectChangedValue = optionSelected => {
    setFormErrors(oldErrors => ({
      ...oldErrors,
      powerDistributorId: false,
      powerDistributor: false
    }))
    setFormData(data => ({
      ...data,
      powerDistributorId: optionSelected?.value,
      powerDistributor: optionSelected?.label
    }))
  }

  return (
    <div className={styles.container}>
      <div className={styles.heroImage}>
        {!!headerContent && headerContent}
        <Image
          src={background.path}
          alt="Energia solar residencial Portal Solar"
          width={1920}
          height={480}
          placeholder="blur"
          blurDataURL={background.path}
          sizes="(max-width: 768px) 100vw, 1920px"
        />
      </div>
      <div className={styles.content}>
        <p className={styles.title}>
          Quer saber quanto custa para instalar <span>energia solar?</span>
        </p>
        <form className="form" onSubmit={handleSubmit}>
          <label
            className="inputLabel font-workSans text-v2-sonic-silver"
            htmlFor="postalCode"
          >
            Insira seu CEP:
          </label>
          <div className={styles.inputBlock}>
            <IMaskInput
              mask="00000-000"
              className={cl(styles.input, 'input')}
              type="text"
              id="postalCode"
              name="postalCode"
              placeholder="00000-000"
              value={formData.postalCode}
              onAccept={value => {
                handleChangedValue({
                  target: { name: 'postalCode', value }
                })
              }}
              tabIndex={1}
            />
            {formErrors?.postalCode && <small>Você deve inserir o CEP</small>}
            {!formErrors?.postalCode && msgError && <small>{msgError}</small>}
          </div>

          <label
            className="inputLabel font-workSans text-v2-sonic-silver"
            htmlFor="lightBill"
          >
            Quanto você gasta com luz?
          </label>
          <div className={styles.inputBlock}>
            <CurrencyInput
              id="lightBill"
              name="lightBill"
              className={cl(
                styles.input,
                'input',
                `${formData.lightBill ? 'text-black' : 'text-v2-sonic-silver'}`
              )}
              placeholder="R$ 0,00"
              decimalsLimit={2}
              decimalSeparator=","
              groupSeparator="."
              prefix="R$ "
              value={formData.lightBill}
              onValueChange={value => {
                handleChangedValue({
                  target: { name: 'lightBill', value: value || '' }
                })
              }}
              tabIndex={2}
            />
            {formErrors?.lightBill && (
              <small>Você deve inserir o gasto de luz</small>
            )}
          </div>
          <label
            className="inputLabel font-workSans text-v2-sonic-silver"
            htmlFor="powerDistributor"
          >
            Qual é a sua concessionária?
          </label>
          <div className={styles.inputBlock}>
            <ReactSelect
              name="powerDistributorId"
              id="powerDistributorId"
              instanceId="powerDistributorId"
              options={powerDistributors.map(item => {
                return { label: item.name, value: item.id }
              })}
              value={
                formData.powerDistributorId && {
                  value: formData.powerDistributorId,
                  label: formData.powerDistributor
                }
              }
              placeholder="Selecione uma concessionária"
              noOptionsMessage={() => 'Nenhuma distribuidora'}
              onChange={handleSelectChangedValue}
              styles={stylesSelect()}
              isDisabled={powerDistributors.length === 0}
              tabIndex={3}
              isClearable={true}
            />
            {formErrors?.powerDistributorId && (
              <small>Você deve inserir uma distribuidora</small>
            )}
          </div>
          <label
            className="inputLabel font-workSans text-v2-sonic-silver"
            htmlFor="email"
          >
            Digite o seu e-mail
          </label>
          <div className={styles.inputBlock}>
            <input
              placeholder="E-mail"
              name="email"
              id="email"
              className={cl(styles.input, 'input')}
              tabIndex={4}
              onChange={handleChangedValue}
              value={formData.email}
            />
            {formErrors?.email && (
              <small>Você deve inserir um e-mail válido</small>
            )}
          </div>
          <div>
            <button
              type="submit"
              className="btn-orange w-full mb-10 disabled:"
              disabled={!!loading}
              tabIndex={5}
            >
              {loading ? (
                <BeatLoader color="#FFF" size="10px" />
              ) : (
                'Simule seu gerador de energia'
              )}
            </button>
          </div>
          <small className="block text-center text-xs font-light text-v2-sonic-silver font-workSans">
            Ao prosseguir você está de acordo com os Termos e Condições de Uso
            do Portal Solar
          </small>
        </form>
      </div>
    </div>
  )
}

export default SimulationBanner
